import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Alert,
} from '@mui/material';
import { Warning as QuarantineIcon, Gavel, CheckCircle, Cancel } from '@mui/icons-material';

const API = '/api/wmc';

interface QuarantineLot {
  id: string;
  lot_number: string;
  material_name: string | null;
  material_code: string | null;
  supplier_name: string | null;
  supplier_lot_number: string | null;
  status: string;
  quantity_available: string;
  unit: string;
  location: string | null;
  expiry_date: string | null;
  days_in_current_status: number;
  notes: string | null;
  created_at: string;
}

const WmcQuarantine: React.FC = () => {
  const [lots, setLots] = useState<QuarantineLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [dispositionDialog, setDispositionDialog] = useState<QuarantineLot | null>(null);
  const [actionType, setActionType] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [deviationNumber, setDeviationNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadLots();
  }, []);

  const loadLots = async () => {
    setLoading(true);
    try {
      // Load quarantine, inspection, and on_hold lots
      const results: QuarantineLot[] = [];
      for (const status of ['quarantine', 'inspection', 'on_hold']) {
        const res = await fetch(`${API}/lots/?status=${status}`);
        const data = await res.json();
        results.push(...(data.results || []));
      }
      setLots(results);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDisposition = async () => {
    if (!dispositionDialog || !actionType || !performedBy) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/lots/${dispositionDialog.id}/disposition/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action_type: actionType,
          performed_by: performedBy,
          notes,
          deviation_number: deviationNumber || undefined,
        }),
      });
      if (res.ok) {
        setDispositionDialog(null);
        setActionType('');
        setPerformedBy('');
        setNotes('');
        setDeviationNumber('');
        loadLots();
      }
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const statusConfig: Record<string, { color: string; label: string }> = {
    quarantine: { color: '#F59E0B', label: 'Quarantine' },
    inspection: { color: '#3B82F6', label: 'Inspection' },
    on_hold: { color: '#8B5CF6', label: 'On Hold' },
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QuarantineIcon /> Quarantine Management
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(statusConfig).map(([status, cfg]) => {
          const count = lots.filter(l => l.status === status).length;
          return (
            <Grid item xs={4} key={status}>
              <Card sx={{ borderLeft: `4px solid ${cfg.color}` }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{cfg.label}</Typography>
                  <Typography variant="h4" sx={{ color: cfg.color }}>{count}</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {lots.filter(l => l.days_in_current_status > 7).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(245,158,11,0.1)' }}>
          {lots.filter(l => l.days_in_current_status > 7).length} lot(s) have been in quarantine/hold for more than 7 days
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>Lot #</TableCell>
              <TableCell>Material</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Days Held</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lots.map(lot => {
              const sc = statusConfig[lot.status] || { color: '#6B7280', label: lot.status };
              const overdue = lot.days_in_current_status > 7;
              return (
                <TableRow key={lot.id} hover sx={{
                  backgroundColor: overdue ? 'rgba(245,158,11,0.05)' : undefined,
                }}>
                  <TableCell><Typography variant="body2" fontWeight="bold">{lot.lot_number}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">{lot.material_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{lot.material_code}</Typography>
                  </TableCell>
                  <TableCell>{lot.supplier_name || '—'}</TableCell>
                  <TableCell>
                    <Chip label={sc.label} size="small" sx={{ backgroundColor: `${sc.color}20`, color: sc.color, fontWeight: 'bold' }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: overdue ? '#EF4444' : '#D1D5DB', fontWeight: overdue ? 'bold' : 'normal' }}>
                      {lot.days_in_current_status}d
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{parseFloat(lot.quantity_available).toFixed(1)} {lot.unit}</TableCell>
                  <TableCell>{lot.location || '—'}</TableCell>
                  <TableCell>{lot.expiry_date || '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Gavel />}
                      onClick={() => setDispositionDialog(lot)}
                      sx={{ textTransform: 'none' }}
                    >
                      Dispose
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {lots.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CheckCircle sx={{ color: '#10B981', fontSize: 40, mb: 1 }} />
                  <Typography color="text.secondary">All clear — no lots in quarantine</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Disposition Dialog */}
      <Dialog open={!!dispositionDialog} onClose={() => setDispositionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
          Disposition — {dispositionDialog?.lot_number}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ p: 2, backgroundColor: '#111827', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Material:</strong> {dispositionDialog?.material_name}</Typography>
              <Typography variant="body2"><strong>Supplier:</strong> {dispositionDialog?.supplier_name}</Typography>
              <Typography variant="body2"><strong>Quantity:</strong> {dispositionDialog?.quantity_available} {dispositionDialog?.unit}</Typography>
              <Typography variant="body2"><strong>Current Status:</strong> {dispositionDialog?.status}</Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Disposition Action</InputLabel>
              <Select value={actionType} onChange={e => setActionType(e.target.value)} label="Disposition Action">
                <MenuItem value="inspect">🔍 Move to Inspection</MenuItem>
                <MenuItem value="accept">✅ Accept — Release to Stock</MenuItem>
                <MenuItem value="reject">❌ Reject — Move to MRB</MenuItem>
                <MenuItem value="hold">⏸ Place on Hold</MenuItem>
                <MenuItem value="accept_on_concession">⚠️ Accept on Concession</MenuItem>
                <MenuItem value="return_to_supplier">📦 Return to Supplier</MenuItem>
                <MenuItem value="scrap">🗑 Scrap</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Performed By"
              value={performedBy}
              onChange={e => setPerformedBy(e.target.value)}
              required
              placeholder="Inspector name"
            />

            {actionType === 'accept_on_concession' && (
              <TextField
                label="Deviation Number"
                value={deviationNumber}
                onChange={e => setDeviationNumber(e.target.value)}
                placeholder="DEV-2026-XXX"
              />
            )}

            <TextField
              label="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Disposition notes, justification..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setDispositionDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDisposition}
            disabled={!actionType || !performedBy || submitting}
          >
            {submitting ? 'Processing...' : 'Apply Disposition'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmcQuarantine;
